import { ArrowIcon } from "@images/svgIcons/arrowIcon"
import { ChronoIcon } from "@images/svgIcons/chronoIcon"
import { CopyIcon } from "@images/svgIcons/copyIcon"
import { EditIcon } from "@images/svgIcons/editIcon"
import { TrashIcon } from "@images/svgIcons/trashIcon"
import { ChronoType } from "@modules/chrono/chronoTypes"
import { convertChronoDraftIntoChrono, displayTotalTime, getTrainingChronoTitle } from "@modules/chrono/chronoUtils"
import { word } from "@modules/core/utils/i18n"
import { ScoreType } from "@modules/scoreboard/scoreTypes"
import { getScoreTypesArray } from "@modules/scoreboard/scoreUtils"
import { RpeTag } from "@modules/sessionBlock/exercises/components/rpeTag"
import { convertChronoFormikToChronoDraft } from "@modules/sessionBlock/exercises/exerciseFormikTypes"
import { getDisplayExerciseCategory } from "@modules/sessionBlock/exercises/exerciseUtils"
import { useBlockDuration } from "@modules/sessionBlock/hooks/useBlockDuration"
import { SessionBlockExerciseFormikType } from "@modules/sessionBlock/sessionBlockFormikTypes"
import { useSessionFormContext } from "@modules/sessions/hooks/useSessionFormContext"
import { useSessionSidebar } from "@modules/sessions/hooks/useSessionSidebar"
import { EllipsisMenu } from "@modules/ui/components/ellipsisMenu"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { HURichTextView } from "@modules/ui/components/richText/huRichTextView"
import { formatTextToRichText } from "@modules/ui/components/richText/richTextUtils"
import { MenuItem } from "primereact/menuitem"
import { useMemo } from "react"
import styled, { useTheme } from "styled-components"

type exerciseExistingBlocProps = {
  block: SessionBlockExerciseFormikType
  blockIndex: number
}

export const ExerciseExistingBloc: React.FC<exerciseExistingBlocProps> = ({ block, blockIndex }) => {
  const theme = useTheme()
  const { formik, copyBlock, moveBlock, deleteBlock } = useSessionFormContext()
  const { getBlockDurationInterval } = useBlockDuration(formik.values.blocks)
  const { navigateToExerciseForm } = useSessionSidebar()

  const scoreTypes = useMemo(() => getScoreTypesArray(), [])

  const displayChronoTime = useMemo(() => {
    const chrono = convertChronoDraftIntoChrono(block.content) || block.content.chrono

    if (block.content.chronoType && block.content.chronoType !== "NONE" && chrono) {
      const chronoDraft = convertChronoFormikToChronoDraft(block.content)
      return (
        getTrainingChronoTitle(block.content.chronoType as ChronoType) +
        " - " +
        (chrono && chronoDraft ? displayTotalTime(chronoDraft) : word("chrono.title.no_chrono"))
      )
    }
    if (chrono === undefined) {
      return word("chrono.title.no_chrono")
    }
    return chrono ? chrono.toString() : word("chrono.title.no_chrono")
  }, [block.content])

  const displayScoreType = useMemo(() => {
    const scoreTypeLabel = scoreTypes.find((scoreTypeLabel) => block.content.scoreType === scoreTypeLabel.value)
    return scoreTypeLabel?.label || ""
  }, [block.content])
  const ellipsisItems: MenuItem[] = [
    {
      items: [
        {
          label: word("sessionForm.blocks.move.up"),
          icon: (
            <ArrowIcon
              arrowDirection="top"
              color={blockIndex === 0 ? theme.neutralColor300 : theme.neutralColor900}
              width={16}
              height={16}
            />
          ),
          visible: blockIndex !== 0 && formik.values.blocks.length >= 1,
          command: () => moveBlock(blockIndex, "UP"),
        },
        {
          label: word("sessionForm.blocks.move.down"),
          icon: (
            <ArrowIcon
              arrowDirection="bottom"
              color={blockIndex === formik.values.blocks.length - 1 ? theme.neutralColor300 : theme.neutralColor900}
              width={16}
              height={16}
            />
          ),
          visible: blockIndex < formik.values.blocks.length - 1 && formik.values.blocks.length >= 1,
          command: () => moveBlock(blockIndex, "DOWN"),
        },
        {
          label: word("program.exercise.copy.label"),
          icon: <CopyIcon color={theme.neutralColor900} width={20} height={20} />,
          command: () => copyBlock(blockIndex),
        },

        {
          label: word("sessionForm.exercise.remove_exo.button"),
          icon: <TrashIcon color={theme.neutralColor900} width={20} height={20} />,
          command: () => deleteBlock(blockIndex),
        },
      ].filter((item) => item.visible !== false),
    },
  ]

  return (
    <div>
      {/* Block duration interval */}
      {!!block.content.durationInMin && (
        <HUText fontStyle="BS" className="flex flex-row align-items-center gap-2 mb-1">
          <div className="bg-neutral-700" style={{ width: 10, height: 10, borderRadius: 3 }} />
          {getBlockDurationInterval(blockIndex)}
        </HUText>
      )}

      <div className={"flex flex-column card p-3 gap-3"}>
        {/* Exercise card header */}
        <div className="flex flex-column">
          <HUText fontStyle="TS" color={theme.neutralColor700}>
            {getDisplayExerciseCategory(block.content.category, block.content.otherCategory)}
          </HUText>
        </div>

        {/* Exercise card content */}
        <div className="flex flex-column">
          <div className="flex align-items-center justify-content-between mb-2">
            <div className="flex align-items-center">
              <ChronoIcon color={theme.buttonPrimaryTextColor} />
              <HUText className="ml-2" fontStyle="LM">
                {displayChronoTime}
              </HUText>
            </div>

            {block.content.rpe !== null && block.content.rpe !== undefined && <RpeTag exercise={block.content} />}
          </div>

          <HURichTextView value={formatTextToRichText(block.content.description)} paragrapheFontStyle="BM" />

          {block.content.scoreType && block.content.scoreType !== ScoreType.NO_SCORE && (
            <div className="flex align-items-center mt-4 mb-2">
              <HUText fontStyle="LL">{word("program.session.score")}</HUText>

              <Separator />

              <HUText fontStyle="LL" className="mr-2">
                {displayScoreType}
              </HUText>
            </div>
          )}

          {block.content.requiredMedia && (
            <div className="flex flex-row align-items-center justify-content-start mt-4 mb-2 gap-2">
              <span className="pi pi-video" style={{ color: theme.textWarning }} />
              <HUText fontStyle="BS">{word("program.planning.create.exercise.media_requirement.required")}</HUText>
            </div>
          )}
        </div>

        {/* Exercise card footer*/}
        <div className="flex flex-row align-items-center justify-content-end">
          <EllipsisMenu items={ellipsisItems} style={{ height: "2.2rem", width: "2.2rem" }} />

          <HUButton
            type="Rounded"
            colorType="Primary"
            size="M"
            icon={{
              icon: () => <EditIcon color={theme.neutralColor900} width={16} height={16} />,
            }}
            style={{ height: "2.2rem", width: "2.2rem" }}
            onClick={() => navigateToExerciseForm(false, blockIndex, block)}
            isTooltipDisabled={false}
            tooltipContent={word("global.edit")}
            tooltipId={"edit-exercise" + blockIndex}
          />
        </div>
      </div>
    </div>
  )
}

const Separator = styled.div`
  display: flex;
  width: 115px;
  height: 1px;
  background-color: ${({ theme }) => theme.neutralColor200};
  margin-left: 15px;
  margin-right: 15px;
`
